import { useMemo, useState } from 'react';
import ReportsNav from '../../components/ReportsNav';
import { Link } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { periodsApi, snapshotsApi, companyAccountsApi, type DiagnosticSnapshot } from '../../api/periods';
import { analyticsApi, type AttentionInboxDto } from '../../api/analytics';
import { getWorkspaceId } from '../../api/workspaces';
import { sendCopilotMessage } from '../../components/CopilotPanel';
import InboxItemCard from '../../components/analytics/InboxItemCard';
import { viewToParams, type DrillView } from '../../components/analytics/drillView';
import ConfirmDialog from '../../components/ConfirmDialog';
import { useReportScreen } from '../../components/ScreenContext';
import { formatMoney } from '../../utils/formatMoney';

/**
 * Uždarymo vedlys — the guided closure: "follow these steps and complete your
 * last year's close." Built entirely on existing backend pieces: the period-
 * scoped diagnostic (honest checklist), the always-rolled-back dry-run (full
 * assertion report + P&L preview), the real annual close with AGM date, and the
 * auto-captured pre-close snapshot (the achievement numbers). The real close is
 * always the accountant's explicit click.
 */

const STEPS = ['overview', 'cleanup', 'dryrun', 'close', 'achievement'] as const;
type Step = (typeof STEPS)[number];

export default function CloseWizard() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [step, setStep] = useState<Step>('overview');
  // Deep link: a MonthEndReadiness close card lands here with ?month=YYYY-MM —
  // the wizard opens on THAT period instead of the default guess.
  const [searchParams] = useSearchParams();
  const linkedMonth = searchParams.get('month');
  const [closeDate, setCloseDate] = useState(() =>
    linkedMonth && /^\d{4}-\d{2}$/.test(linkedMonth)
      ? `${linkedMonth}-01`
      : defaultCloseDate());
  const [isAnnual, setIsAnnual] = useState(true);
  const [agmDate, setAgmDate] = useState('');
  const [dryRunDone, setDryRunDone] = useState(false);
  const [closeResult, setCloseResult] = useState<{ ok: boolean; message: string } | null>(null);
  const [askClose, setAskClose] = useState(false);
  const [closeError, setCloseError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [settingAccounts, setSettingAccounts] = useState(false);
  const [accountsSet, setAccountsSet] = useState(false);

  // ── Step 1: Apžvalga (diagnostic) ──
  const { data: diag, isLoading: diagLoading } = useQuery({
    queryKey: ['close-diagnostic', closeDate, isAnnual],
    queryFn: () => periodsApi.diagnostic(closeDate, isAnnual),
    enabled: step === 'overview' || step === 'cleanup',
    staleTime: 60_000,
    retry: false,
    // NEVER collapse on a date/annuality change — the previous diagnostic stays
    // visible while the new one fetches (the #1 flicker cause).
    placeholderData: (prev: DiagnosticSnapshot | undefined) => prev,
  });

  // ── Step 2: current-state items (honestly labeled, NOT period-scoped) ──
  const { data: inbox } = useQuery({
    queryKey: ['analytics-inbox'],
    queryFn: analyticsApi.inbox,
    enabled: step === 'cleanup',
    staleTime: 30_000,
    retry: false,
  });

  // ── Step 3: dry-run ──
  const { data: dry, isFetching: dryBusy } = useQuery({
    queryKey: ['close-dryrun', closeDate, isAnnual],
    queryFn: () => periodsApi.autoCloseDryRun(closeDate, isAnnual, agmDate || undefined),
    enabled: step === 'dryrun',
    staleTime: 60_000,
    retry: false,
  });

  // ── Step 5: achievement (snapshot after a real close) ──
  const { data: snapshot } = useQuery({
    queryKey: ['pre-close-snapshots', closeDate],
    queryFn: () => snapshotsApi.list(closeDate),
    enabled: step === 'achievement' && closeResult?.ok === true,
    staleTime: 60_000,
    retry: false,
  });
  const income = useMemo(() => {
    const first = snapshot?.[0];
    if (!first) return null;
    try {
      // Keys are PASCALCase server-side (TotalIncome/TotalExpenses/NetProfit).
      return JSON.parse(first.incomeStatementJson) as {
        TotalIncome: number; TotalExpenses: number; NetProfit: number;
      };
    } catch {
      return null;
    }
  }, [snapshot]);

  const errors = diag?.preCloseErrors ?? [];
  const warnings = diag?.preCloseWarnings ?? [];
  // Already-closed is a DONE state — the checklist rows would only add noise
  // for a period that no longer needs anything.
  const alreadyClosed = (diag?.period.blockers ?? []).some(b => b.code === 'alreadyClosed');
  const blocked = !!diag && (!diag.period.canClose || errors.length > 0 || diag.unpostedJournalEntries > 0);

  // Copilot screen report (house pattern: JournalList) — the wizard's state in
  // one line: step, blocker + checklist counts, close date; the counts ride IN
  // the summary so data changes re-report.
  const blockers = diag?.period.blockers ?? [];
  const issueCount = blockers.length + errors.length + warnings.length;
  const screenFilters: Record<string, string> = { zingsnis: step, metinis: isAnnual ? 'taip' : 'ne' };
  if (closeDate) screenFilters.data = closeDate.slice(0, 10);
  const screenPreview = [
    ...blockers.map(b =>
      `Blokas: ${b.code}${b.count != null ? ` (${b.count})` : ''}${b.date ? ` data: ${b.date.slice(0, 10)}` : ''}`),
    ...errors.map(e => `Klaida: ${asciiLt(e)}`),
    ...warnings.map(w => `Ispejimas: ${asciiLt(w)}`),
  ].slice(0, 10).map(l => asciiLt(l).substring(0, 120));
  useReportScreen(
    `Uzdarymas (zingsnis: ${step}, ${blockers.length} bloku, ${errors.length} klaidu, ${warnings.length} ispejimu${closeDate ? `, data: ${closeDate.slice(0, 10)}` : ''})`,
    {
      recordCount: issueCount,
      filters: screenFilters,
      preview: screenPreview,
    },
  );

  // AI egress is per-workspace — a Tirti button that silently drops the question
  // (copilot closed / egress off) is a dead click.
  const { data: aiStatus } = useQuery({
    queryKey: ['ai-status', getWorkspaceId()],
    queryFn: () => analyticsApi.aiStatus(getWorkspaceId() ?? ''),
    staleTime: 60_000,
    retry: false,
  });
  const aiEnabled = aiStatus?.aiEnabled ?? false;

  const investigateChecklist = (text: string) => {
    if (!aiEnabled) return;
    sendCopilotMessage(
      t('closeWizard.investigateTemplate', { period: closeDate.slice(0, 10), item: text }));
  };

  const doClose = async () => {
    setAskClose(false);
    setBusy(true);
    setCloseError(null);
    try {
      await periodsApi.autoClose(closeDate, isAnnual, agmDate || undefined);
      setCloseResult({ ok: true, message: '' });
      setStep('achievement');
    } catch (err) {
      const message = (err as Error).message || t('closeWizard.closeFailed');
      setCloseError(message.includes('403')
        ? t('closeWizard.forbidden')
        : message);
      setCloseResult({ ok: false, message });
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="space-y-4">
      <ReportsNav />
      {/* Header + period */}
      <div className="flex items-baseline gap-3 pt-1">
        <h1 className="text-xl font-semibold text-gray-900">{t('closeWizard.title')}</h1>
        <p className="text-xs text-gray-400">{t('closeWizard.subtitle')}</p>
      </div>

      <div className="bg-white border border-gray-200 rounded-lg p-4 flex flex-wrap items-end gap-4">
        <label className="text-sm">
          <span className="block text-xs text-gray-500 mb-1 font-medium">{t('closeWizard.closeDate')}</span>
          <input
            type="date"
            value={closeDate}
            onChange={e => { setCloseDate(e.target.value); setDryRunDone(false); setCloseResult(null); }}
            className="border border-gray-200 rounded-lg px-2 py-1.5 text-sm"
          />
        </label>
        <label className="text-sm flex items-center gap-2 pb-2">
          <input type="checkbox" checked={isAnnual}
                 onChange={e => { setIsAnnual(e.target.checked); setDryRunDone(false); }} />
          {t('closeWizard.annual')}
        </label>
        <div className="flex-1" />
        {diag?.lastClosedPeriodDate && (
          <div className="text-xs text-gray-400 pb-2">
            {t('closeWizard.lastClosed')}: {new Date(diag.lastClosedPeriodDate).toLocaleDateString('lt-LT')}
          </div>
        )}
      </div>

      {/* Stepper */}
      <div className="flex items-center gap-2 flex-wrap">
        {STEPS.map((s, i) => (
          <button
            key={s}
            onClick={() => s !== 'close' && s !== 'achievement' && setStep(s)}
            disabled={s === 'dryrun' && blocked}
            className={`px-3 py-1.5 text-xs font-medium rounded-full border transition-colors ${
              step === s ? 'bg-primary-600 text-white border-primary-600'
                : s === 'dryrun' && blocked ? 'bg-gray-50 text-gray-300 border-gray-100'
                : 'bg-white text-gray-600 border-gray-200 hover:border-primary-300'}`}
          >
            {i + 1}. {t(`closeWizard.step.${s}`)}
          </button>
        ))}
      </div>

      {/* ONE persistent card shell — the step content swaps INSIDE it, so switching
          steps never re-shapes the page (the flicker fix). */}
      <div data-testid="wizard-shell" className="bg-white border border-gray-200 rounded-lg p-5 space-y-4 min-h-[480px]">

      {/* ── 1. Apžvalga ── */}
      {step === 'overview' && (
        <div className="space-y-3">
          {diagLoading && !diag && <p className="text-sm text-gray-400">{t('common.loading')}</p>}
          {diagLoading && diag && <p className="text-xs text-gray-400">{t('closeWizard.refreshing')}</p>}
          {diag && (
            <>
              {/* Būklė — typed blockers, each its own Lithuanian state. The old
                  concatenated English blockReason became this. alreadyClosed is
                  NOT an error: a green "done" state replaces the red wall. */}
              {(diag.period.blockers ?? []).some(b => b.code === 'alreadyClosed') ? (
                <div className="rounded-lg bg-green-50 border border-green-200 px-3 py-3 space-y-1">
                  <p className="text-sm text-green-800 font-medium">
                    ✓ {t('closeWizard.alreadyClosed', {
                      date: new Date(diag.period.blockers.find(b => b.code === 'alreadyClosed')!.date ?? closeDate)
                        .toLocaleDateString('lt-LT') })}
                  </p>
                  <p className="text-xs text-green-700">{t('closeWizard.alreadyClosedHint')}</p>
                </div>
              ) : (
                <>
                  {(diag.period.blockers ?? []).filter(b => b.code === 'dateInvalid').map((b, i) => (
                    <div key={`bd${i}`} className="rounded-lg bg-red-50 border border-red-200 px-3 py-2 text-sm text-red-700">
                      ⛔ {t('closeWizard.dateInvalid', {
                        last: b.date ? new Date(b.date).toLocaleDateString('lt-LT') : '' })}
                    </div>
                  ))}
                  {(diag.period.blockers ?? []).some(b => b.code === 'checklistErrors') && errors.length > 0 && (
                    <div className="rounded-lg bg-red-50 border border-red-200 px-3 py-2 text-sm text-red-700">
                      ⛔ {t('closeWizard.checklistErrorsCount', {
                        count: diag.period.blockers.find(b => b.code === 'checklistErrors')?.count ?? errors.length })}
                    </div>
                  )}
                </>
              )}
              {/* Closing-accounts gap: SUGGEST the accounts mined from the last close;
                  the user confirms — the app never edits the book silently. */}
              {diag.closingAccounts && !diag.closingAccounts.configured && !accountsSet && (
                <div className="rounded-lg bg-amber-50 border border-amber-200 px-3 py-3 space-y-2">
                  <p className="text-sm text-amber-800 font-medium">{t('closeWizard.missingAccounts')}</p>
                  {diag.closingAccounts.proposedCurrentProfit != null ? (
                    <p className="text-xs text-amber-700">
                      {t('closeWizard.proposedAccounts', {
                        from: diag.closingAccounts.proposedFromCloseDate
                          ? new Date(diag.closingAccounts.proposedFromCloseDate).toLocaleDateString('lt-LT') : '',
                        summary: diag.closingAccounts.proposedClosingSummary ?? 0,
                        current: diag.closingAccounts.proposedCurrentProfit ?? 0,
                        former: diag.closingAccounts.proposedFormerProfit ?? 0,
                      })}
                    </p>
                  ) : (
                    <p className="text-xs text-amber-700">{t('closeWizard.noProposal')}</p>
                  )}
                  <div className="flex justify-end">
                    <button
                      onClick={async () => {
                        setSettingAccounts(true);
                        try {
                          const ca = diag.closingAccounts!;
                          if (ca.proposedClosingSummary != null)
                            await companyAccountsApi.set('ClosingSummary', ca.proposedClosingSummary);
                          if (ca.proposedCurrentProfit != null)
                            await companyAccountsApi.set('CurrentProfit', ca.proposedCurrentProfit);
                          if (ca.proposedFormerProfit != null)
                            await companyAccountsApi.set('FormerProfit', ca.proposedFormerProfit);
                          setAccountsSet(true);
                          await queryClient.invalidateQueries({ queryKey: ['close-diagnostic'] });
                        } finally {
                          setSettingAccounts(false);
                        }
                      }}
                      disabled={settingAccounts || diag.closingAccounts.proposedCurrentProfit == null}
                      className="px-4 py-1.5 text-xs font-medium text-primary-700 bg-white border border-primary-200 rounded-lg hover:border-primary-400 disabled:opacity-40"
                    >
                      {settingAccounts ? t('common.saving') : t('closeWizard.setAccounts')}
                    </button>
                  </div>
                </div>
              )}
              {accountsSet && (
                <div className="rounded-lg bg-green-50 border border-green-100 px-3 py-2 text-sm text-green-700">
                  ✓ {t('closeWizard.accountsSet')}
                </div>
              )}
              {/* Hygiene: unposted entries AFTER the close date don't block it — but pollute. */}
              {(diag.unpostedAfterCloseDate ?? 0) > 0 && (
                <div className="rounded-lg bg-gray-50 border border-gray-200 px-3 py-2 text-sm text-gray-600">
                  ⚠ {t('closeWizard.unpostedAfter', { count: diag.unpostedAfterCloseDate ?? 0 })}
                </div>
              )}
              {!alreadyClosed && (<>
              {errors.map((e, i) => (
                <div key={i} className="flex items-start gap-2 rounded-lg bg-red-50 border border-red-100 px-3 py-2">
                  <span className="text-sm text-red-700 flex-1">{e}</span>
                  {aiEnabled && (<button onClick={() => investigateChecklist(e)} className="text-xs text-violet-600 hover:text-violet-800 font-medium whitespace-nowrap">
                    {t('closeWizard.investigate')}
                  </button>)}
                </div>
              ))}
              {warnings.map((w, i) => (
                <div key={i} className="flex items-start gap-2 rounded-lg bg-amber-50 border border-amber-100 px-3 py-2">
                  <span className="text-sm text-amber-800 flex-1">{w}</span>
                  {aiEnabled && (<button onClick={() => investigateChecklist(w)} className="text-xs text-violet-600 hover:text-violet-800 font-medium whitespace-nowrap">
                    {t('closeWizard.investigate')}
                  </button>)}
                </div>
              ))}
              </>)}
              {diag.unpostedJournalEntries > 0 && (
                <div className="rounded-lg bg-red-50 border border-red-100 px-3 py-2 text-sm text-red-700 flex items-center justify-between gap-2">
                  <span>{t('closeWizard.unposted', { count: diag.unpostedJournalEntries })}</span>
                  <button onClick={() => navigate('/journal')}
                          className="text-xs font-medium text-red-700 underline hover:text-red-900 whitespace-nowrap">
                    {t('closeWizard.openJournal')} →
                  </button>
                </div>
              )}
              <div className="grid grid-cols-2 gap-3 pt-1">
                <div className="text-sm">
                  <div className="text-[10px] uppercase font-semibold text-gray-400">{t('closeWizard.trialBalance')}</div>
                  {diag.trialBalance.isBalanced
                    ? <span className="text-green-700">✓ {t('closeWizard.balanced')}</span>
                    : <span className="text-red-600">{t('closeWizard.imbalance', { amount: formatMoney(diag.trialBalance.imbalance) })}</span>}
                </div>
                <div className="text-sm">
                  <div className="text-[10px] uppercase font-semibold text-gray-400">{t('closeWizard.pAndLClasses')}</div>
                  {/* Wire fields are netBalance/accountClass — the old `balance` read was
                      undefined, so the non-zero filter NEVER filtered (the zero wall). */}
                  {(() => {
                    const nonZero = (diag.accountBalances ?? []).filter(b => Math.abs(b.netBalance) > 0.005);
                    if (nonZero.length === 0) return <span className="text-green-700">✓ {t('closeWizard.classesZero')}</span>;
                    const shown = nonZero.slice(0, 8)
                      .map(b => `${b.accountNumber}: ${formatMoney(b.netBalance)}`).join(', ');
                    return (
                      <span className="text-gray-700">
                        {shown}{nonZero.length > 8 ? ` ${t('closeWizard.andMore', { count: nonZero.length - 8 })}` : ''}
                      </span>
                    );
                  })()}
                </div>
              </div>
              <div className="flex items-center justify-end gap-3 pt-2">
                {blocked && (
                  <span className="text-xs text-red-600">{t('closeWizard.continueBlocked')}</span>
                )}
                <button
                  onClick={() => setStep('cleanup')}
                  disabled={blocked}
                  className="px-4 py-2 text-sm font-medium text-white bg-primary-600 rounded-lg hover:bg-primary-700 disabled:opacity-40"
                >
                  {t('closeWizard.continue')} →
                </button>
              </div>
            </>
          )}
        </div>
      )}

      {/* ── 2. Išvalymas ── */}
      {step === 'cleanup' && (
        <div className="space-y-4">
            <h3 className="text-sm font-semibold text-gray-700 mb-2">
              {t('closeWizard.forClose', { date: closeDate.slice(0, 10) })}
            </h3>
            {/* The checklist already rendered on the overview step — this step's job
                is the CURRENT-STATE work (the inbox cards below), not a re-listing. */}
            {errors.length === 0 && warnings.length === 0
              ? <p className="text-sm text-green-700">✓ {t('closeWizard.noChecklistIssues')}</p>
              : <p className="text-sm text-gray-500">
                  {t('closeWizard.checklistOnOverview', { errors: errors.length, warnings: warnings.length })}
                </p>}
          <div className="border-t border-gray-100 pt-3">
            <h3 className="text-sm font-semibold text-gray-700 mb-1">
              {t('closeWizard.currentState')}
            </h3>
            <p className="text-xs text-gray-400 mb-3">{t('closeWizard.currentStateHint')}</p>
            <div className="space-y-1">
              {((inbox as AttentionInboxDto | undefined)?.items ?? [])
                .filter(i => !i.automated)
                .slice(0, 15)
                .map(item => (
                  <InboxItemCard key={`${item.agentName}-${item.stableKey ?? item.title}`} item={item}
                                 onDrill={(v: DrillView) => navigate(`/analytics?${new URLSearchParams(viewToParams(v)).toString()}`)} />
                ))}
            </div>
          </div>
          <div className="flex justify-between">
            <button onClick={() => setStep('overview')} className="px-4 py-2 text-sm text-gray-600 border border-gray-200 rounded-lg hover:bg-gray-50">
              ← {t('closeWizard.back')}
            </button>
            <button
              onClick={() => setStep('dryrun')}
              className="px-4 py-2 text-sm font-medium text-white bg-primary-600 rounded-lg hover:bg-primary-700"
            >
              {t('closeWizard.toDryRun')} →
            </button>
          </div>
        </div>
      )}

      {/* ── 3. Bandomasis uždarymas ── */}
      {step === 'dryrun' && (
        <div className="space-y-3">
          <h3 className="text-sm font-semibold text-gray-700">{t('closeWizard.dryRunTitle')}</h3>
          {!dry && dryBusy && <p className="text-sm text-gray-400">{t('closeWizard.dryRunRunning')}</p>}
          {dry && dryBusy && <p className="text-xs text-gray-400">{t('closeWizard.refreshing')}</p>}
          {dry && (
            <>
              {dry.success ? (
                <div className="rounded-lg bg-green-50 border border-green-100 px-3 py-2 text-sm text-green-700">
                  ✓ {t('closeWizard.dryRunPassed', { lines: dry.linesCreated })}
                </div>
              ) : (
                <div className="rounded-lg bg-red-50 border border-red-200 px-3 py-2 text-sm text-red-700">
                  ⛔ {dry.error ?? t('closeWizard.dryRunFailed')}
                </div>
              )}
              <div className="grid grid-cols-2 gap-3">
                <div className="text-sm">
                  <div className="text-[10px] uppercase font-semibold text-gray-400">{t('closeWizard.closingDebits')}</div>
                  <div className="text-gray-800 tabular-nums">{formatMoney(dry.totalClosingDebits)}</div>
                </div>
                <div className="text-sm">
                  <div className="text-[10px] uppercase font-semibold text-gray-400">{t('closeWizard.netResult')}</div>
                  <div className={`tabular-nums font-medium ${dry.totalClosingCredits - dry.totalClosingDebits >= 0 ? 'text-green-700' : 'text-red-600'}`}>
                    {formatMoney(dry.totalClosingCredits - dry.totalClosingDebits)}
                  </div>
                </div>
              </div>
              {dry.failedAssertions.map((a, i) => (
                <div key={i} className="text-xs text-red-600">✗ {a}</div>
              ))}
              {dry.warnings.map((w, i) => (
                <div key={i} className="text-xs text-amber-700">⚠ {w}</div>
              ))}
              {isAnnual && dry.profitAppropriationCompleted === false && (
                <div className="text-xs text-amber-700">{t('closeWizard.agmSkipped')}</div>
              )}
              <div className="flex justify-between pt-2">
                <button onClick={() => setStep('cleanup')} className="px-4 py-2 text-sm text-gray-600 border border-gray-200 rounded-lg hover:bg-gray-50">
                  ← {t('closeWizard.back')}
                </button>
                <button
                  onClick={() => { setDryRunDone(true); setStep('close'); }}
                  disabled={!dry.success}
                  className="px-4 py-2 text-sm font-medium text-white bg-primary-600 rounded-lg hover:bg-primary-700 disabled:opacity-40"
                >
                  {t('closeWizard.toClose')} →
                </button>
              </div>
            </>
          )}
        </div>
      )}

      {/* ── 4. Uždarymas ── */}
      {step === 'close' && (
        <div className="space-y-4">
          <h3 className="text-sm font-semibold text-gray-700">{t('closeWizard.closeTitle', { date: closeDate.slice(0, 10) })}</h3>
          {closeError && (
            <div className="rounded-lg bg-red-50 border border-red-200 px-3 py-2 text-sm text-red-700">{closeError}</div>
          )}
          {isAnnual && (
            <label className="text-sm block">
              <span className="block text-xs text-gray-500 mb-1 font-medium">{t('closeWizard.agmDate')}</span>
              <input type="date" value={agmDate} onChange={e => setAgmDate(e.target.value)}
                     className="border border-gray-200 rounded-lg px-2 py-1.5 text-sm" />
              <span className="block text-[11px] text-gray-400 mt-1">{t('closeWizard.agmHint')}</span>
            </label>
          )}
          <div className="flex items-center justify-between">
            <Link to="/periods/close" className="text-xs text-gray-400 hover:text-gray-600 underline">
              {t('closeWizard.manualClose')}
            </Link>
            <div className="flex gap-2">
              <button onClick={() => setStep('dryrun')} className="px-4 py-2 text-sm text-gray-600 border border-gray-200 rounded-lg hover:bg-gray-50">
                ← {t('closeWizard.back')}
              </button>
              <button
                onClick={() => setAskClose(true)}
                disabled={!dryRunDone || busy}
                className="px-5 py-2 text-sm font-medium text-white bg-red-600 rounded-lg hover:bg-red-700 disabled:opacity-40"
              >
                {t('closeWizard.closeNow')}
              </button>
            </div>
          </div>
          {askClose && (
            <ConfirmDialog
              open={askClose}
              title={t('closeWizard.confirmTitle')}
              message={t('closeWizard.confirmMessage', { date: closeDate.slice(0, 10) })}
              confirmLabel={t('closeWizard.closeNow')}
              onConfirm={doClose}
              onCancel={() => setAskClose(false)}
            />
          )}
        </div>
      )}

      {/* ── 5. Pasiekimas ── */}
      {step === 'achievement' && closeResult?.ok && (
        <div className="rounded-lg border border-green-200 bg-green-50/40 p-8 text-center space-y-4">
          <div className="mx-auto flex items-center justify-center w-16 h-16 rounded-full bg-green-50 border border-green-200">
            <svg className="w-8 h-8 text-green-600" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
            </svg>
          </div>
          <h2 className="text-lg font-semibold text-gray-900">
            {t('closeWizard.achievement', { date: closeDate.slice(0, 4) })}
          </h2>
          {income && (
            <div className="grid grid-cols-3 gap-4 pt-2">
              <div>
                <div className="text-[10px] uppercase font-semibold text-gray-400">{t('closeWizard.income')}</div>
                <div className="text-lg font-semibold text-gray-900 tabular-nums">{formatMoney(income.TotalIncome)}</div>
              </div>
              <div>
                <div className="text-[10px] uppercase font-semibold text-gray-400">{t('closeWizard.expenses')}</div>
                <div className="text-lg font-semibold text-gray-900 tabular-nums">{formatMoney(income.TotalExpenses)}</div>
              </div>
              <div>
                <div className="text-[10px] uppercase font-semibold text-gray-400">{t('closeWizard.netProfit')}</div>
                <div className={`text-lg font-bold tabular-nums ${income.NetProfit >= 0 ? 'text-green-700' : 'text-red-600'}`}>
                  {formatMoney(income.NetProfit)}
                </div>
              </div>
            </div>
          )}
          <p className="text-xs text-gray-400 pt-2">{t('closeWizard.achievementHint')}</p>
          <div className="border-t border-gray-100 pt-3 text-left max-w-md mx-auto space-y-1">
            <p className="text-xs font-semibold text-gray-500 uppercase">{t('closeWizard.whatsNext')}</p>
            <p className="text-xs text-gray-500">{t('closeWizard.nextStatements')}</p>
            <p className="text-xs text-gray-500">{t('closeWizard.nextDeclaration')}</p>
            <p className="text-[11px] text-gray-400">{t('closeWizard.nextOutside')}</p>
          </div>
        </div>
      )}

      </div>{/* /wizard-shell */}
    </div>
  );
}

/** Default target: Dec 31 of the last year that has no closing entry yet —
 *  for the classic case, last year (2025-12-31). */
function defaultCloseDate(): string {
  const now = new Date();
  const lastYear = now.getFullYear() - 1;
  return `${lastYear}-12-31`;
}

/** Strip Lithuanian diacritics from copilot screen strings (ASCII-only house style). */
function asciiLt(s: string): string {
  const map: Record<string, string> = {
    'ą': 'a', 'č': 'c', 'ę': 'e', 'ė': 'e', 'į': 'i', 'š': 's', 'ų': 'u', 'ū': 'u', 'ž': 'z',
    'Ą': 'A', 'Č': 'C', 'Ę': 'E', 'Ė': 'E', 'Į': 'I', 'Š': 'S', 'Ų': 'U', 'Ū': 'U', 'Ž': 'Z',
    // Backend checklist strings carry typographic punctuation — fold the common
    // ones (em/en dash, quotes), then strip any residual non-ASCII.
    '—': '-', '–': '-', '„': '"', '”': '"', '“': '"',
    '‘': "'", '’': "'",
  };
  return s.replace(/[ąčęėįšųūžĄČĘĖĮŠŲŪŽ—–„”“‘’]/g, c => map[c] ?? c)
    .replace(/[^\x20-\x7E]/g, '');
}
